import type { EmailMessageType } from "@/types/email";
import { EmailDirection } from "@/types/enums/email";

/**
 * Context describing the email being rendered into the sandboxed iframe.
 * Normalizations read from this to decide whether they apply and what CSS to
 * contribute. Add fields here as new normalizations need more signal — this is
 * the single "meta" object injected into every normalization.
 */
export interface EmailRenderContext {
  /** Resolved dark/light mode for the email surface. */
  isDark: boolean;
  /** Direction of the message (OUTBOUND = composed in-app by us). */
  direction?: EmailDirection;
  /**
   * What kind of mail this is. Direction can't distinguish a conversational reply from a
   * marketing blast — both are OUTBOUND — so normalizations that only make sense for
   * conversational mail key on this.
   */
  messageType?: EmailMessageType;
}

/**
 * A single, self-contained transform applied to email content before it is
 * rendered in the sandboxed iframe. Each normalization declares when it applies
 * and the CSS it injects — so extending behavior is one entry in the registry
 * below, never a new boolean prop on the renderer.
 */
export interface EmailContentNormalization {
  id: string;
  appliesTo: (ctx: EmailRenderContext) => boolean;
  css: (ctx: EmailRenderContext) => string;
}

/**
 * Dark mode: invert the email's content (which assumes a light background)
 * while re-inverting media so photos/logos/illustrations still read correctly.
 */
const darkModeInvert: EmailContentNormalization = {
  id: "dark-mode-invert",
  appliesTo: (ctx) => ctx.isDark,
  css: () => `
    body > * { filter: invert(1) hue-rotate(180deg); }
    img, video, picture, svg, [style*="background-image"] { filter: invert(1) hue-rotate(180deg); }
  `,
};

/**
 * Light mode: neutralize stark-white wrappers so the email blends into the
 * app's cream reading surface instead of sitting in a white box. Only pure
 * white is targeted — intentional colors (buttons, colored sections, background
 * images) are preserved.
 */
const lightModeNeutralizeWhite: EmailContentNormalization = {
  id: "light-mode-neutralize-white",
  appliesTo: (ctx) => !ctx.isDark,
  // NOTE: short-hex inline backgrounds (`#fff`) are intentionally NOT matched.
  // CSS substring selectors (`*=`) can't anchor a word boundary, so a
  // `[style*="background:#fff" i]` selector also matches `background:#fff000`
  // (yellow) and wrongly neutralizes it. `bgcolor` short-hex is safe because it
  // uses an exact-match selector, not a substring. Six-digit `#ffffff` and
  // `rgb(255, 255, 255)` substrings have no such collision.
  css: () => `
    [bgcolor="#FFFFFF" i], [bgcolor="#FFF" i], [bgcolor="white" i],
    [style*="background:#ffffff" i], [style*="background: #ffffff" i],
    [style*="background-color:#ffffff" i], [style*="background-color: #ffffff" i],
    [style*="background:rgb(255,255,255)" i], [style*="background: rgb(255, 255, 255)" i],
    [style*="background-color:rgb(255,255,255)" i], [style*="background-color: rgb(255, 255, 255)" i] {
      background-color: transparent !important;
    }
  `,
};

/**
 * Conversational mail we composed in-app (a compose/reply/forward, i.e. CUSTOM) is
 * wrapped by the email composer in a centered, max-width, padded container — right for a
 * marketing blast, wrong for a reply. Strip that wrapper's framing so our sent mail reads
 * as flush, full-width content, the way a Gmail reply renders.
 *
 * A sent blast is OUTBOUND too but deliberately keeps the marketing scaffold, so this is
 * gated on the message type rather than direction alone. Inbound mail is likewise left
 * untouched so external/marketing layouts render faithfully.
 */
const outboundFullBleed: EmailContentNormalization = {
  id: "outbound-full-bleed",
  appliesTo: (ctx) =>
    ctx.direction === EmailDirection.OUTBOUND && ctx.messageType === "CUSTOM",
  css: () => `
    body > * {
      max-width: none !important;
      margin-inline: 0 !important;
      padding: 0 !important;
    }
  `,
};

/**
 * Registry of content normalizations, applied in order. To add a new transform
 * (strip tracking pixels, force link colors, collapse hidden preheaders, …)
 * append an entry here — no changes to the renderer or its callers.
 */
export const EMAIL_CONTENT_NORMALIZATIONS: EmailContentNormalization[] = [
  darkModeInvert,
  lightModeNeutralizeWhite,
  outboundFullBleed,
];

/** Compose the CSS for every normalization that applies to `ctx`. */
export function buildNormalizationCss(ctx: EmailRenderContext): string {
  return EMAIL_CONTENT_NORMALIZATIONS.filter((n) => n.appliesTo(ctx))
    .map((n) => n.css(ctx))
    .join("\n");
}
